import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import HelpTooltip from '../components/HelpTooltip';
import CancelSurveyModal from '../components/CancelSurveyModal';
import { AppUsageInstructions } from './PwaInstall';
import AdProvider from '../components/AdProvider';
import AdBanner from '../components/AdBanner';
import FullpageAd from '../components/FullpageAd';

const LOGO = '/dailyreps3.png';
const MAX_KIDS = 8;

function ageRangeText(min, max) {
  if (max <= 9) return 'Under 10';
  if (min >= 14) return '14+';
  return '10\u201313';
}

const CURRENT_YEAR = new Date().getFullYear();
const BIRTH_YEARS = Array.from({ length: 22 }, (_, i) => CURRENT_YEAR - 3 - i);

function ageFromBirthYear(birthYear) {
  return CURRENT_YEAR - birthYear;
}

function KidCard({ kid, apiFetch, onUpdate }) {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [resetPasswordMode, setResetPasswordMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [viewAsLoading, setViewAsLoading] = useState(false);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const data = await apiFetch(`/api/parent/kids/${kid.id}/stats`);
      setStats(data);
    } catch (err) {
      console.error('Failed to load kid stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleExpand = () => {
    if (!expanded && !stats) loadStats();
    setExpanded(!expanded);
  };

  const handleResetPassword = async () => {
    setResetPasswordError('');
    if (newPassword.length < 6) {
      setResetPasswordError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setResetPasswordError('Passwords do not match');
      return;
    }
    setResetPasswordLoading(true);
    try {
      await apiFetch(`/api/parent/kids/${kid.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ password: newPassword }),
      });
      setResetPasswordMode(false);
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      setResetPasswordError(err.message);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleDeactivate = async () => {
    const kidDisplayName = kid.first_name ? `${kid.first_name} ${kid.last_name}` : kid.generated_username;
    if (!window.confirm(`Deactivate ${kidDisplayName}? They won't be able to log in until reactivated.`)) return;
    setActionLoading(true);
    try {
      await apiFetch(`/api/parent/kids/${kid.id}/deactivate`, { method: 'PUT' });
      onUpdate();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await apiFetch(`/api/parent/kids/${kid.id}`, { method: 'DELETE' });
      onUpdate();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="kid-card">
      <div className="kid-card-header" onClick={handleExpand} style={{ cursor: 'pointer' }}>
        <div>
          <div className="kid-card-username">{kid.first_name ? `${kid.first_name} ${kid.last_name}` : kid.generated_username}</div>
          <div className="kid-card-meta">
            {kid.first_name && kid.generated_username && <span>{kid.generated_username}</span>}
            {kid.first_name && kid.generated_username && kid.age_range_min != null && <span> · </span>}
            {!kid.first_name && kid.soccer_word ? `${kid.soccer_word} + ${kid.creature}` : ''}
            {!kid.first_name && kid.soccer_word && kid.age_range_min != null && <span> · </span>}
            {kid.age_range_min != null && (
              <span>Age {ageFromBirthYear(kid.birth_year)} ({ageRangeText(kid.age_range_min, kid.age_range_max)})</span>
            )}
          </div>
        </div>
        <span style={{ color: 'var(--text-muted)', fontSize: '1.2rem' }}>{expanded ? '\u25B2' : '\u25BC'}</span>
      </div>

      {expanded && (
        <div className="kid-card-details">
          {statsLoading ? (
            <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-muted)' }}>Loading stats...</div>
          ) : stats ? (
            <>
              <div className="kid-stats-grid">
                <div className="kid-stat">
                  <div className="kid-stat-value">{stats.current_streak}</div>
                  <div className="kid-stat-label">Streak</div>
                </div>
                <div className="kid-stat">
                  <div className="kid-stat-value">{stats.total_completions}</div>
                  <div className="kid-stat-label">Completions</div>
                </div>
                <div className="kid-stat">
                  <div className="kid-stat-value">{stats.total_points}</div>
                  <div className="kid-stat-label">Points</div>
                </div>
                <div className="kid-stat">
                  <div className="kid-stat-value">{stats.level?.name || 'Rookie'}</div>
                  <div className="kid-stat-label">Level</div>
                </div>
              </div>
              {stats.badges && stats.badges.length > 0 && (
                <div className="kid-badges">
                  {stats.badges.map((b, i) => (
                    <span key={i} className="kid-badge-emoji" title={b.name}>{b.icon_emoji}</span>
                  ))}
                </div>
              )}
            </>
          ) : null}

          {/* Login info */}
          <div style={{
            background: 'var(--dark-bg)', border: '1px solid var(--card-border)', borderRadius: 8,
            padding: 12, marginBottom: 12, fontSize: '0.85rem'
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--orange)' }}>
              <i className="fas fa-key" style={{ marginRight: 4 }}></i> Login Info
            </div>
            <div style={{ lineHeight: 1.8 }}>
              {kid.team_name && <div><strong>Team:</strong> {kid.team_name}</div>}
              <div><strong>Username:</strong> {kid.username || kid.generated_username}</div>
              <div><strong>Login URL:</strong> {window.location.origin}/player-login</div>
            </div>
          </div>

          <button
            className="btn"
            style={{ width: '100%', marginBottom: 12, background: 'var(--blue)', color: '#fff', border: 'none' }}
            disabled={viewAsLoading}
            onClick={async () => {
              setViewAsLoading(true);
              try {
                const data = await apiFetch(`/api/parent/kids/${kid.id}/view-as`, { method: 'POST' });
                startImpersonation(data.token, data.user, data.team ? [data.team] : [], 'parent');
                navigate('/player');
              } catch (err) {
                console.error('View as player error:', err);
              } finally {
                setViewAsLoading(false);
              }
            }}
          >
            {viewAsLoading ? 'Loading...' : 'View as Player'}
          </button>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: '0.85rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!kid.self_manage}
              onChange={async () => {
                try {
                  await apiFetch(`/api/parent/kids/${kid.id}/self-manage`, {
                    method: 'PUT',
                    body: JSON.stringify({ self_manage: !kid.self_manage }),
                  });
                  onUpdate();
                } catch (err) {
                  console.error('Toggle self-manage error:', err);
                }
              }}
              style={{ accentColor: 'var(--orange)', width: 16, height: 16 }}
            />
            Allow Player to Manage Account
          </label>

          <div className="kid-card-actions">
            {resetPasswordMode ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                {resetPasswordError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem' }}>{resetPasswordError}</div>}
                <input
                  type="password"
                  placeholder="New Password (min 6 characters)" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--dark-bg)', color: '#fff', fontSize: '0.9rem' }}
                />
                <input
                  type="password"
                  placeholder="Confirm Password" value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--dark-bg)', color: '#fff', fontSize: '0.9rem' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-sm btn-orange" disabled={resetPasswordLoading} onClick={handleResetPassword}>
                    {resetPasswordLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button className="btn btn-sm btn-outline" onClick={() => { setResetPasswordMode(false); setNewPassword(''); setConfirmNewPassword(''); setResetPasswordError(''); }}>Cancel</button>
                </div>
              </div>
            ) : confirmDelete ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                <div style={{ color: 'var(--danger)', fontSize: '0.85rem', fontWeight: 600 }}>
                  This will permanently delete {kid.first_name ? `${kid.first_name} ${kid.last_name}` : kid.generated_username} and all their training data. This cannot be undone.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-sm btn-danger" disabled={actionLoading} onClick={handleDelete}>
                    {actionLoading ? 'Deleting...' : 'Yes, Delete Forever'}
                  </button>
                  <button className="btn btn-sm btn-outline" onClick={() => setConfirmDelete(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-sm btn-outline" onClick={() => setResetPasswordMode(true)}>Reset Password</button>
                <button className="btn btn-sm btn-outline" disabled={actionLoading} onClick={handleDeactivate}>Deactivate</button>
                <button className="btn btn-sm btn-danger" onClick={() => setConfirmDelete(true)}>Delete</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BillingSection({ apiFetch }) {
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [billingSuccess, setBillingSuccess] = useState('');
  const [showCancelSurvey, setShowCancelSurvey] = useState(false);

  const loadBilling = useCallback(async () => {
    setBillingLoading(true);
    try {
      const data = await apiFetch('/api/parent/billing');
      setBilling(data);
    } catch (err) { console.error(err); }
    finally { setBillingLoading(false); }
  }, [apiFetch]);

  useEffect(() => { loadBilling(); }, [loadBilling]);

  const handlePortal = async () => {
    setBillingError(''); setSaving(true);
    try {
      const data = await apiFetch('/api/parent/billing/portal', { method: 'POST' });
      window.location.href = data.portal_url;
    } catch (err) { setBillingError(err.message); }
    finally { setSaving(false); }
  };

  const handleCancel = async (cancelReason) => {
    setShowCancelSurvey(false);
    setBillingError(''); setSaving(true);
    try {
      const data = await apiFetch('/api/parent/billing/cancel', {
        method: 'POST',
        body: JSON.stringify({ cancel_reason: cancelReason }),
      });
      setBillingSuccess(`Canceled. Access until ${new Date(data.access_until).toLocaleDateString()}.`);
      loadBilling();
    } catch (err) { setBillingError(err.message); }
    finally { setSaving(false); }
  };

  const handleReactivate = async () => {
    setBillingError(''); setSaving(true);
    try {
      await apiFetch('/api/parent/billing/reactivate', { method: 'POST' });
      setBillingSuccess('Subscription reactivated.');
      loadBilling();
    } catch (err) { setBillingError(err.message); }
    finally { setSaving(false); }
  };

  const handleUpgrade = async () => {
    setBillingError(''); setSaving(true);
    try {
      await apiFetch('/api/parent/upgrade', { method: 'POST' });
      setBillingSuccess('Upgraded to Family plan.');
      loadBilling();
    } catch (err) { setBillingError(err.message); }
    finally { setSaving(false); }
  };

  if (billingLoading) return null;

  // Trial pending: show activation prompt
  if (billing?.trial_pending) {
    return (
      <div style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: '1rem', marginBottom: 12, color: 'var(--text-muted)' }}>Billing</h3>
        <div style={{ background: 'rgba(243,156,18,0.12)', border: '2px solid #f39c12', borderRadius: 12, padding: 20, textAlign: 'center' }}>
          <h3 style={{ color: '#f39c12', margin: '0 0 8px' }}>Complete Your Registration</h3>
          <p style={{ color: '#555', fontSize: '0.9rem', margin: '0 0 16px' }}>
            Start your 30-day free trial to unlock unlimited daily training.
          </p>
          <a href="/parent/activate" className="btn btn-orange" style={{ textDecoration: 'none', display: 'inline-block' }}>
            Complete Registration - Start Free Trial
          </a>
          <p style={{ fontSize: '0.75rem', color: '#999', marginTop: 12 }}>
            {billing.pending_plan === 'family' ? 'Family' : 'Individual'} plan, {billing.pending_interval === 'annual' ? 'annual' : 'monthly'} billing. 30-day free trial. Cancel anytime before day 30 and pay nothing.
          </p>
        </div>
      </div>
    );
  }

  if (!billing?.subscription) return null;

  const sub = billing.subscription;
  const isIndividual = billing.plan_type === 'individual';
  const perKidSubs = billing.subscriptions || [];

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ fontSize: '1rem', marginBottom: 12, color: 'var(--text-muted)' }}>Billing</h3>

      {billingError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 8 }}>{billingError}</div>}
      {billingSuccess && <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginBottom: 8 }}>{billingSuccess}</div>}

      {sub.status === 'past_due' && (
        <div className="billing-past-due-banner">
          Payment failed. Update your payment method.
          <button className="btn btn-sm btn-outline" onClick={handlePortal} disabled={saving} style={{ marginLeft: 8 }}>Update</button>
        </div>
      )}

      {sub.canceled_at && (
        <div className="billing-canceled-notice">
          Ends {new Date(sub.current_period_end).toLocaleDateString()}.
          <button className="btn btn-sm btn-outline" onClick={handleReactivate} disabled={saving} style={{ marginLeft: 8 }}>Reactivate</button>
        </div>
      )}

      {/* Main plan card */}
      <div className="card" style={{ marginBottom: isIndividual && perKidSubs.length > 1 ? 8 : 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>{sub.plan === 'family' ? 'Family' : 'Individual'}</strong>
          <div className="billing-status-badge" data-status={sub.status}>
            {sub.status === 'trialing'
              ? (sub.trial_end || sub.current_period_end
                  ? `Free Trial - Ends ${new Date(sub.trial_end || sub.current_period_end).toLocaleDateString()}`
                  : 'Free Trial')
              : sub.status}
          </div>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
          {isIndividual
            ? `${billing.kids_count} player${billing.kids_count !== 1 ? 's' : ''} \u2022 billed per player`
            : `${billing.kids_count} of ${billing.max_kids} player${billing.max_kids !== 1 ? 's' : ''}`}
          {sub.billing_interval && ` \u2022 ${sub.billing_interval}`}
        </div>
        {sub.current_period_end && sub.status !== 'trialing' && !isIndividual && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
            Next bill: {new Date(sub.current_period_end).toLocaleDateString()}
          </div>
        )}
        {sub.card_last4 && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 6 }}>
            {sub.card_brand && sub.card_brand.charAt(0).toUpperCase() + sub.card_brand.slice(1)} ending in {sub.card_last4}
          </div>
        )}
      </div>

      {/* Per-kid subscription cards (individual plan only) */}
      {isIndividual && perKidSubs.length > 1 && perKidSubs.map(kidSub => (
        <div key={kidSub.id} className="card" style={{ marginBottom: 6, padding: '10px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem' }}>{kidSub.player_name || 'Player'}</span>
            <div className="billing-status-badge" data-status={kidSub.status} style={{ fontSize: '0.7rem', padding: '2px 8px' }}>
              {kidSub.status === 'trialing'
                ? (kidSub.trial_end || kidSub.current_period_end
                    ? `Trial - Ends ${new Date(kidSub.trial_end || kidSub.current_period_end).toLocaleDateString()}`
                    : 'Trial')
                : kidSub.status}
            </div>
          </div>
          {kidSub.canceled_at && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
              Ends {new Date(kidSub.current_period_end).toLocaleDateString()}
            </div>
          )}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <a href="/parent/account" className="btn btn-sm btn-outline" style={{ textDecoration: 'none' }}>Manage Account</a>
        {isIndividual && perKidSubs.length >= 2 && (
          <button className="btn btn-sm btn-outline" onClick={handleUpgrade} disabled={saving}>Upgrade to Family</button>
        )}
        {!sub.canceled_at && (
          <button className="btn btn-sm btn-outline" onClick={() => setShowCancelSurvey(true)} disabled={saving} style={{ color: '#e74c3c' }}>Cancel</button>
        )}
      </div>

      {showCancelSurvey && (
        <CancelSurveyModal
          onConfirm={handleCancel}
          onClose={() => setShowCancelSurvey(false)}
        />
      )}
    </div>
  );
}

function HowToUseSection() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
      >
        <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
          <i className="fas fa-circle-question" style={{ color: 'var(--orange)', marginRight: 8 }}></i>
          How to Use Train Daily
        </div>
        <span style={{ color: 'var(--text-muted)', fontSize: '1.2rem' }}>{expanded ? '\u25B2' : '\u25BC'}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 16 }}>
          <AppUsageInstructions textColor="#fff" />
        </div>
      )}
    </div>
  );
}

function InactiveKidsSection({ kids, apiFetch, onUpdate }) {
  const [reactivating, setReactivating] = useState({});
  const [errors, setErrors] = useState({});

  if (kids.length === 0) return null;

  const handleReactivate = async (kidId) => {
    setReactivating(prev => ({ ...prev, [kidId]: true }));
    setErrors(prev => ({ ...prev, [kidId]: '' }));
    try {
      await apiFetch(`/api/parent/kids/${kidId}/reactivate-individual`, { method: 'POST' });
      onUpdate();
    } catch (err) {
      const parsed = typeof err.message === 'string' ? err.message : 'Failed to reactivate';
      setErrors(prev => ({ ...prev, [kidId]: parsed }));
    } finally {
      setReactivating(prev => ({ ...prev, [kidId]: false }));
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ fontSize: '1rem', color: 'var(--text-muted)', marginBottom: 12 }}>Inactive Players</h3>
      {kids.map(kid => {
        const name = kid.first_name ? `${kid.first_name} ${kid.last_name}` : kid.generated_username;
        const statusText = kid.team_name ? `Deactivated on ${kid.team_name}` : 'Removed from team';
        return (
          <div key={kid.id} className="card" style={{ marginBottom: 8, padding: '12px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>{name}</strong>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>{statusText}</div>
              </div>
              <button
                className="btn btn-sm btn-orange"
                disabled={reactivating[kid.id]}
                onClick={() => handleReactivate(kid.id)}
              >
                {reactivating[kid.id] ? 'Reactivating...' : 'Reactivate as Individual'}
              </button>
            </div>
            {errors[kid.id] && (
              <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: 8 }}>
                {errors[kid.id]}
                {errors[kid.id].includes('subscription') && (
                  <span> <a href="/parent/activate" style={{ color: 'var(--orange)' }}>Set up subscription</a></span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ParentDashboard() {
  const { user, apiFetch, logout } = useAuth();
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddKid, setShowAddKid] = useState(false);
  const [billing, setBilling] = useState(null);

  const loadKids = useCallback(async () => {
    try {
      const data = await apiFetch('/api/parent/kids');
      setKids(data.kids);
    } catch (err) {
      console.error('Failed to load kids:', err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const loadBilling = useCallback(async () => {
    try {
      const data = await apiFetch('/api/parent/billing');
      setBilling(data);
    } catch (err) { console.error(err); }
  }, [apiFetch]);

  useEffect(() => { loadKids(); loadBilling(); }, [loadKids, loadBilling]);

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <AdProvider>
    <div className="parent-dashboard">
      <div className="parent-header">
        <div className="parent-header-left">
          <img src={LOGO} alt="Train Daily" />
          <h2>Hi, {user.first_name}</h2>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <a href="/parent/account" className="btn btn-sm btn-outline" style={{ textDecoration: 'none' }}>Manage Account</a>
          <button className="btn btn-sm btn-outline" onClick={logout}>Log Out</button>
        </div>
      </div>
      <AdBanner placement="top" />

      {billing?.trial_pending && (
        <div style={{ background: 'rgba(243,156,18,0.12)', border: '2px solid #f39c12', borderRadius: 12, padding: 20, textAlign: 'center', marginBottom: 20 }}>
          <h3 style={{ color: '#f39c12', margin: '0 0 8px' }}>Complete Your Registration</h3>
          <p style={{ color: '#555', fontSize: '0.9rem', margin: '0 0 16px' }}>
            Start your 30-day free trial to unlock unlimited daily training.
          </p>
          <a href="/parent/activate" className="btn btn-orange" style={{ textDecoration: 'none', display: 'inline-block' }}>
            Complete Registration - Start Free Trial
          </a>
          <p style={{ fontSize: '0.75rem', color: '#999', marginTop: 12 }}>
            30-day free trial. Cancel anytime before day 30 and pay nothing.
          </p>
        </div>
      )}

      <HowToUseSection />

      {kids.filter(k => k.status === 'active').length === 0 && kids.filter(k => k.status === 'inactive').length === 0 && !showAddKid && (
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>No players yet. Add your first player to get started.</p>
        </div>
      )}

      {kids.filter(k => k.status === 'active').map(kid => (
        <KidCard key={kid.id} kid={kid} apiFetch={apiFetch} onUpdate={loadKids} />
      ))}

      {showAddKid ? (
        <AddKidForm
          apiFetch={apiFetch}
          onSuccess={() => { setShowAddKid(false); loadKids(); }}
          onCancel={() => setShowAddKid(false)}
        />
      ) : kids.filter(k => k.status === 'active').length < MAX_KIDS && (
        <button
          className="btn btn-orange"
          onClick={() => setShowAddKid(true)}
          style={{ width: '100%', marginTop: 16 }}
        >
          {kids.filter(k => k.status === 'active').length > 0 ? 'Add Additional Player' : 'Add Player'}
        </button>
      )}

      <InactiveKidsSection kids={kids.filter(k => k.status === 'inactive')} apiFetch={apiFetch} onUpdate={loadKids} />

      <BillingSection apiFetch={apiFetch} />

      <AdBanner placement="bottom" />
      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <a href="/parent/merch" style={{ color: 'var(--orange)', fontWeight: 600, fontSize: '0.95rem' }}>
          {'\uD83D\uDED2'} Shop Merch
        </a>
        <span style={{ margin: '0 12px', color: 'var(--text-muted)' }}>|</span>
        <a href="/parent/faq" style={{ color: 'var(--orange)', fontWeight: 600, fontSize: '0.95rem' }}>
          FAQ
        </a>
        <span style={{ margin: '0 12px', color: 'var(--text-muted)' }}>|</span>
        <a href="/support" style={{ color: 'var(--orange)', fontWeight: 600, fontSize: '0.95rem' }}>
          Support
        </a>
      </div>
    </div>
    <FullpageAd />
    </AdProvider>
  );
}

function AddKidForm({ apiFetch, onSuccess, onCancel }) {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [kidPassword, setKidPassword] = useState('');
  const [confirmKidPassword, setConfirmKidPassword] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [lockSoccerWord, setLockSoccerWord] = useState(null);
  const [lockCreature, setLockCreature] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('name');
  const [showFamilyUpgradePrompt, setShowFamilyUpgradePrompt] = useState(false);
  const [newKidId, setNewKidId] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [consentLanguage, setConsentLanguage] = useState('');
  const [privacyPolicyVersion, setPrivacyPolicyVersion] = useState('');
  const [consentName, setConsentName] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);

  const needsConsent = () => {
    if (!birthYear) return false;
    return ageFromBirthYear(parseInt(birthYear)) < 13;
  };

  const generateOptions = useCallback(async (lockSoccer = null, lockCr = null) => {
    setLoadingOptions(true);
    setError('');
    try {
      let data;
      if (lockSoccer || lockCr) {
        data = await apiFetch('/api/username/generate', {
          method: 'POST',
          body: JSON.stringify({ lock_soccer_word: lockSoccer, lock_creature: lockCr }),
        });
      } else {
        data = await apiFetch('/api/username/generate');
      }
      setOptions(data.options);
      setSelectedOption(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingOptions(false);
    }
  }, [apiFetch]);

  const handleNextFromBirthYear = () => {
    if (!birthYear) { setError('Please enter birth year'); return; }
    const currentYear = new Date().getFullYear();
    const yr = parseInt(birthYear);
    if (yr < currentYear - 25 || yr > currentYear - 3) {
      setError('Please enter a valid birth year');
      return;
    }
    setError('');
    if (needsConsent()) {
      setConsentLoading(true);
      if (!consentName && user) {
        setConsentName(`${user.first_name} ${user.last_name}`);
      }
      apiFetch('/api/parent/consent-language')
        .then(data => {
          setConsentLanguage(data.consent_language);
          setPrivacyPolicyVersion(data.privacy_policy_version);
          setStep('consent');
        })
        .catch(() => setError('Failed to load consent information'))
        .finally(() => setConsentLoading(false));
    } else {
      setStep('username');
      generateOptions();
    }
  };

  const handleLockSoccerWord = (word) => {
    const newLock = lockSoccerWord === word ? null : word;
    setLockSoccerWord(newLock);
    generateOptions(newLock, lockCreature);
  };

  const handleLockCreature = (creature) => {
    const newLock = lockCreature === creature ? null : creature;
    setLockCreature(newLock);
    generateOptions(lockSoccerWord, newLock);
  };

  const handleShuffle = () => {
    setLockSoccerWord(null);
    setLockCreature(null);
    generateOptions(null, null);
  };

  const getAgeDisplay = () => {
    if (!birthYear) return '';
    const age = ageFromBirthYear(parseInt(birthYear));
    if (age <= 9) return `Age ${age} \u2014 Under 10 group`;
    if (age <= 13) return `Age ${age} \u2014 10\u201313 group`;
    return `Age ${age} \u2014 14+ group`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOption) { setError('Please select a username'); return; }
    if (kidPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (kidPassword !== confirmKidPassword) { setError('Passwords do not match'); return; }

    setSubmitting(true);
    setError('');
    try {
      const body = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        birth_year: parseInt(birthYear),
        password: kidPassword,
        generated_username: selectedOption.generated_username,
        soccer_word: selectedOption.soccer_word,
        creature: selectedOption.creature,
      };
      if (needsConsent()) {
        body.consent_accepted = true;
        body.parent_consent_name = consentName.trim();
      }
      const data = await apiFetch('/api/parent/kids', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (data.suggest_family_upgrade) {
        setNewKidId(data.kid.id);
        setShowFamilyUpgradePrompt(true);
        return;
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpgradeToFamily = async () => {
    setUpgrading(true);
    setError('');
    try {
      await apiFetch('/api/parent/upgrade', { method: 'POST' });
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpgrading(false);
    }
  };

  const handleKeepPerKid = async () => {
    setUpgrading(true);
    setError('');
    try {
      await apiFetch(`/api/parent/kids/${newKidId}/confirm-billing`, { method: 'POST' });
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpgrading(false);
    }
  };

  if (showFamilyUpgradePrompt) {
    return (
      <div className="kid-card" style={{ marginTop: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Upgrade to Family Plan?</h3>
        {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 12 }}>{error}</div>}
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 16 }}>
          You now have 3 or more players. The Family plan covers up to 8 players under a single subscription instead of billing per player.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-orange" onClick={handleUpgradeToFamily} disabled={upgrading}>
            {upgrading ? 'Upgrading...' : 'Upgrade to Family'}
          </button>
          <button className="btn btn-outline" onClick={handleKeepPerKid} disabled={upgrading}>
            Keep Per-Player Billing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="kid-card" style={{ marginTop: 16 }}>
      <h3 style={{ marginBottom: 12 }}>Add Player</h3>
      {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 12 }}>{error}</div>}

      {step === 'name' && (
        <div>
          <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 6 }}>First Name</label>
          <input
            type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--dark-bg)', color: '#fff', fontSize: '1rem', marginBottom: 12 }}
          />
          <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 6 }}>Last Name</label>
          <input
            type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--dark-bg)', color: '#fff', fontSize: '1rem', marginBottom: 12 }}
          />
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: 12 }}>
            Only you see this name. Players log in with a username.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-orange" type="button" onClick={() => {
              if (!firstName.trim() || !lastName.trim()) { setError('First and last name are required'); return; }
              setError('');
              setStep('birth_year');
            }} disabled={!firstName.trim() || !lastName.trim()}>
              Next
            </button>
            <button className="btn btn-outline" type="button" onClick={onCancel}>Cancel</button>
          </div>
        </div>
      )}

      {step === 'birth_year' && (
        <div>
          <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 6 }}>Birth Year</label>
          <select
            value={birthYear}
            onChange={(e) => setBirthYear(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--dark-bg)', color: '#fff', fontSize: '1rem', marginBottom: 6 }}
          >
            <option value="">Select year...</option>
            {BIRTH_YEARS.map(y => <option key={y} value={y}>{y} (age {CURRENT_YEAR - y})</option>)}
          </select>
          {birthYear && getAgeDisplay() && (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 12 }}>{getAgeDisplay()}</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-orange" type="button" onClick={handleNextFromBirthYear} disabled={consentLoading}>
              {consentLoading ? 'Loading...' : 'Next'}
            </button>
            <button className="btn btn-outline" type="button" onClick={() => setStep('name')}>Back</button>
          </div>
        </div>
      )}

      {step === 'consent' && (
        <div>
          <h4 style={{ marginBottom: 8, color: 'var(--orange)' }}>Parental Consent Required</h4>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 12 }}>
            Because your child is under 13, federal law (COPPA) requires your consent before creating their account.
          </p>
          <div
            style={{
              background: 'var(--dark-bg)',
              padding: 16,
              borderRadius: 8,
              marginBottom: 16,
              maxHeight: 200,
              overflowY: 'auto',
              fontSize: '0.85rem',
              lineHeight: 1.6,
              border: '1px solid var(--card-border)',
              color: 'var(--text-muted)',
            }}
            dangerouslySetInnerHTML={{ __html: consentLanguage }}
          />
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 12 }}>
            Privacy Policy v{privacyPolicyVersion}
          </p>
          <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 6 }}>
            Your Name (Parent/Guardian)
          </label>
          <input
            type="text"
            placeholder="Full name"
            value={consentName}
            onChange={(e) => setConsentName(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--dark-bg)', color: '#fff', fontSize: '1rem', marginBottom: 12 }}
          />
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 16 }}>
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted(e.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18 }}
            />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              I consent to my child using Train Daily. I have read the consent notice above and the Privacy Policy.
            </span>
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-orange"
              type="button"
              disabled={!consentAccepted || !consentName.trim()}
              onClick={() => { setError(''); setStep('username'); generateOptions(); }}
            >
              Continue
            </button>
            <button className="btn btn-outline" type="button" onClick={() => setStep('birth_year')}>Back</button>
          </div>
        </div>
      )}

      {step === 'username' && (
        <div>
          <label style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 10 }}>
            Choose a username for your player
            <HelpTooltip>
              <h3>Why random usernames?</h3>
              <p>To protect the online identity of children, TrainDaily does not use any personally identifiable information. Usernames are chosen at random.</p>
              <p>If you do not like any of the usernames below, click on the <strong>Shuffle</strong> link and it will allow you to choose from more.</p>
            </HelpTooltip>
          </label>

          <div className="username-picker">
            {loadingOptions ? (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Generating options...</div>
            ) : (
              options.map((opt, i) => (
                <div
                  key={i}
                  className={`username-option ${selectedOption === opt ? 'selected' : ''}`}
                  onClick={() => setSelectedOption(opt)}
                >
                  <div className="username-parts">
                    <span>{opt.generated_username}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          <button className="shuffle-btn" type="button" onClick={handleShuffle} disabled={loadingOptions}>
            {loadingOptions ? 'Shuffling...' : 'Shuffle All'}
          </button>

          {selectedOption && (
            <div style={{ textAlign: 'center', margin: '12px 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Selected: <strong style={{ color: 'var(--orange)' }}>{selectedOption.generated_username}</strong>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-orange" type="button" onClick={() => { if (!selectedOption) { setError('Please select a username'); return; } setError(''); setStep('password'); }} disabled={!selectedOption}>
              Next
            </button>
            <button className="btn btn-outline" type="button" onClick={() => setStep(needsConsent() ? 'consent' : 'birth_year')}>Back</button>
          </div>
        </div>
      )}

      {step === 'password' && (
        <form onSubmit={handleSubmit}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Username</div>
            <div style={{ color: 'var(--orange)', fontWeight: 700, fontSize: '1.1rem' }}>{selectedOption?.generated_username}</div>
          </div>

          <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 6 }}>Password (min 6 characters)</label>
          <input
            type="password"
            placeholder="Password" value={kidPassword}
            onChange={(e) => setKidPassword(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--dark-bg)', color: '#fff', fontSize: '1rem', marginBottom: 8 }}
          />
          <input
            type="password"
            placeholder="Confirm Password" value={confirmKidPassword}
            onChange={(e) => setConfirmKidPassword(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--dark-bg)', color: '#fff', fontSize: '1rem', marginBottom: 12 }}
          />
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: 16 }}>
            Your player will use this password to log in at traindaily.com/player-login. You can reset it anytime.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-orange" type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Player'}
            </button>
            <button className="btn btn-outline" type="button" onClick={() => setStep('username')}>Back</button>
          </div>
        </form>
      )}
    </div>
  );
}
